import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { BankLineSubjectAssociationWithSubject } from '@features/bankTransactions/types/bankLineSubjectAssociationWithSubject'
import {
  useApproveMatchWithPostings,
  useCreateSubjectsAndBankLinesReconciliation,
} from '@features/paymentReconciliationModal/hooks'
import { ReconcilableResponse } from '@features/paymentReconciliationModal/query-api'
import { PayableSubjectType } from '@features/paymentReconciliationModal/types/payableSubjectType'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { BankLine, DifferenceType } from '@modules-deprecated/bankReconciliation/types'

import { BankMatchUiFlowValue, UiSourceValue } from '../enums'
import { BookkeepingItemType } from '../enums/bookkeepingItemType'
import { BookkeepingItem } from '../types/bookkeepingItem'
import {
  convertBookkeepingItemsToReconciliationSubjects,
  getCrossCurrencyExpectedAmount,
  isCrossCurrencyMatch,
} from '../utils'
import { useAddBankLineToNewMatch } from './useAddBankLineToNewMatch'
import { useCombineBankLines } from './useCombineBankLines'

interface UseMatchBankTransactionProps {
  onSuccess?: (data: ReconcilableResponse) => void
  onError?: (errorMessage: string) => void
  uiSource?: UiSourceValue
  uiFlow?: BankMatchUiFlowValue
}

interface MatchTransactionParams {
  selectedTransaction: BankLine
  selectedBookkeepingItems: BookkeepingItem[]
  differenceType?: DifferenceType | null
  accountCurrency?: string
  existingAssociations?: BankLineSubjectAssociationWithSubject[]
  combinedTransactions?: Map<string, BankLine>
}

export const useMatchBankTransaction = (props?: UseMatchBankTransactionProps) => {
  const { onSuccess, onError, uiSource, uiFlow } = props || {}
  const { t } = useTranslation()

  const { organization } = useUserOrganization()
  const organizationId = organization?.id

  const { combineBankLines, isProcessing: isCombiningBankLines } = useCombineBankLines()
  const { addBankLineToNewMatchAsync } = useAddBankLineToNewMatch()

  const { create: createReconciliation, isProcessing: isReconciling } = useCreateSubjectsAndBankLinesReconciliation({
    onSuccess,
    onError: (error) => {
      onError?.(error?.message || t('something_went_wrong'))
    },
  })

  const { approve: approveMatchWithPostings, isProcessing: isApprovingPostings } = useApproveMatchWithPostings({
    onSuccess,
    onError: (error) => {
      onError?.(error?.message || t('something_went_wrong'))
    },
    uiSource,
    uiFlow,
  })

  const matchTransaction = useCallback(
    async ({
      selectedTransaction,
      selectedBookkeepingItems,
      differenceType,
      accountCurrency,
      existingAssociations = [],
      combinedTransactions,
    }: MatchTransactionParams) => {
      if (!selectedTransaction || !organizationId || selectedBookkeepingItems.length === 0) {
        return
      }

      const combinedTransactionsIds = combinedTransactions ? Array.from(combinedTransactions.keys()) : []
      const matchId = selectedTransaction.match?.id

      const postingItemsCount = selectedBookkeepingItems.filter(
        (item) => item.type === BookkeepingItemType.Posting,
      ).length

      // More than 1 posting + difference is not allowed
      if (postingItemsCount > 1 && differenceType) {
        onError?.(t('bank_transactions.drawer.errors.multiple_postings_with_difference'))
        return
      }

      // Validate bank fee account is set
      if (differenceType === 'bankFee' && !organization?.defaultBankFeeAccountId) {
        onError?.(t('bank_transactions.drawer.errors.bank_fee_account_required'))
        return
      }

      const successfullyCombinedIds: string[] = []

      const rollbackCombinedTransactions = () =>
        successfullyCombinedIds.length > 0
          ? Promise.all(
              successfullyCombinedIds.map((id) => {
                const bankLine = combinedTransactions?.get(id)
                return bankLine ? addBankLineToNewMatchAsync(bankLine) : Promise.resolve()
              }),
            )
          : Promise.resolve()

      if (combinedTransactionsIds && combinedTransactionsIds.length > 0) {
        if (!matchId) {
          return
        }

        for (const combinedTransactionId of combinedTransactionsIds) {
          try {
            await combineBankLines({ bankLineId: combinedTransactionId, matchId })
            successfullyCombinedIds.push(combinedTransactionId)
          } catch {
            await rollbackCombinedTransactions()
            onError?.(t('bank_transactions.drawer.errors.grouping_failed'))
            return
          }
        }
      }

      const hasPostingItems = postingItemsCount > 0

      // Flow for matches including any postings
      if (hasPostingItems) {
        if (!matchId) {
          return
        }

        return approveMatchWithPostings(
          {
            matchId,
            subjects: selectedBookkeepingItems.map((item) => ({
              id: item.id,
              type: item.type as unknown as PayableSubjectType,
            })),
            differenceType: differenceType || undefined,
            defaultBankFeeAccountId: organization?.defaultBankFeeAccountId,
            existingAssociations,
          },
          {
            onError: () => rollbackCombinedTransactions(),
          },
        )
      }

      // Flow for matches without postings
      const bankLinesIds = [selectedTransaction.id, ...combinedTransactionsIds]
      const subjects = convertBookkeepingItemsToReconciliationSubjects(selectedBookkeepingItems)
      const isCrossCurrency = isCrossCurrencyMatch(selectedBookkeepingItems, accountCurrency)

      return createReconciliation(
        {
          bankLinesIds,
          organizationId,
          subjects,
          parameters: {
            differenceType: isCrossCurrency ? undefined : differenceType || undefined,
            expectedAmount: isCrossCurrency ? getCrossCurrencyExpectedAmount(selectedBookkeepingItems) : undefined,
            isAutoMatch: false,
          },
          uiSource,
          uiFlow,
        },
        {
          onError: () => rollbackCombinedTransactions(),
        },
      )
    },
    [
      organizationId,
      organization?.defaultBankFeeAccountId,
      createReconciliation,
      onError,
      t,
      combineBankLines,
      approveMatchWithPostings,
      addBankLineToNewMatchAsync,
      uiSource,
      uiFlow,
    ],
  )

  return {
    matchTransaction,
    isProcessing: isReconciling || isApprovingPostings || isCombiningBankLines,
  }
}
